import React, { useEffect, useState } from 'react';
import { FluentProvider, webDarkTheme, webLightTheme } from '@fluentui/react-components';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './context/AuthContext';
import AdminApiKeysPage from './components/AdminApiKeysPage';
import AdminDashboard from './components/AdminDashboard';
import AdminRoute from './components/AdminRoute';
import ApiKeysPage from './components/ApiKeysPage';
import CalendarPage from './components/CalendarPage';
import CustomersPage from './components/CustomersPage';
import Dashboard from './components/Dashboard';
import DayoffsPage from './components/DayoffsPage';
import DeletedEntriesPage from './components/DeletedEntriesPage';
import EmployeesPage from './components/EmployeesPage';
import EntriesPage from './components/EntriesPage';
import MemberTimeSummaryPage from './components/MemberTimeSummaryPage';
import Navigation from './components/Navigation';
import DocumentTitleManager from './components/DocumentTitleManager';
import PrivateRoute from './components/PrivateRoute';
import ProjectTasksPage from './components/ProjectTasksPage';
import ProjectPlanningPage from './components/ProjectPlanningPage';
import ProjectPlanningRoute from './components/ProjectPlanningRoute';
import CustomerProjectPlanningPage from './components/CustomerProjectPlanningPage';
import LeadEditPage from './components/LeadEditPage';
import LeadsPage from './components/LeadsPage';
import ReportsPage from './components/ReportsPage';
import ReportRoute from './components/ReportRoute';
import WeeklyTimesheetPage from './components/WeeklyTimesheetPage';
import './App.css';
import './time.css';

function App() {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'dark' || storedTheme === 'light') {
      return storedTheme === 'dark';
    }
    localStorage.setItem('theme', 'light');
    return false;
  });

  const closeSidebar = () => setSidebarOpen(false);
  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  return (
    <FluentProvider theme={isDarkMode ? webDarkTheme : webLightTheme} className="fluent-app-provider">
      <AuthProvider>
        <Router>
          <DocumentTitleManager />
          <div className={`App ${isSidebarOpen ? 'sidebar-open' : ''}`}>
          <PrivateRoute>
            <div className="main-layout">
              <Navigation
                isMobileOpen={isSidebarOpen}
                onToggle={toggleSidebar}
                onNavigate={closeSidebar}
                isDarkMode={isDarkMode}
                onToggleTheme={() => setIsDarkMode(prev => !prev)}
              />
              <div
                className={`mobile-overlay ${isSidebarOpen ? 'show' : ''}`}
                onClick={closeSidebar}
                aria-hidden={!isSidebarOpen}
              />
              <main className="main-content">
                <div className="main-content-inner">
                  <div className="mobile-header">
                    <button
                      type="button"
                      className="mobile-menu-button"
                      onClick={toggleSidebar}
                      aria-label="Toggle navigation menu"
                      aria-expanded={isSidebarOpen}
                    >
                      <span className="mobile-menu-icon" aria-hidden="true"></span>
                    </button>
                    <div className="mobile-header-title">DevRain Time</div>
                  </div>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route
                      path="/admin-dashboard"
                      element={(
                        <AdminRoute>
                          <AdminDashboard />
                        </AdminRoute>
                      )}
                    />
                    <Route path="/entries" element={<EntriesPage />} />
                    <Route path="/api-keys" element={<ApiKeysPage />} />
                    <Route path="/calendar" element={<CalendarPage />} />
                    <Route path="/weekly" element={<WeeklyTimesheetPage />} />
                    <Route
                      path="/reports"
                      element={(
                        <ReportRoute>
                          <ReportsPage />
                        </ReportRoute>
                      )}
                    />
                    <Route
                      path="/leads"
                      element={(
                        <ReportRoute>
                          <LeadsPage />
                        </ReportRoute>
                      )}
                    />
                    <Route
                      path="/leads/new"
                      element={(
                        <ReportRoute>
                          <LeadEditPage />
                        </ReportRoute>
                      )}
                    />
                    <Route
                      path="/leads/edit/:id"
                      element={(
                        <ReportRoute>
                          <LeadEditPage />
                        </ReportRoute>
                      )}
                    />
                    <Route
                      path="/customers"
                      element={(
                        <ReportRoute>
                          <CustomersPage />
                        </ReportRoute>
                      )}
                    />
                    <Route
                      path="/dayoffs"
                      element={(
                        <AdminRoute>
                          <DayoffsPage />
                        </AdminRoute>
                      )}
                    />
                    <Route
                      path="/member-time-summary"
                      element={(
                        <AdminRoute>
                          <MemberTimeSummaryPage />
                        </AdminRoute>
                      )}
                    />
                    <Route
                      path="/deleted-entries"
                      element={(
                        <AdminRoute>
                          <DeletedEntriesPage />
                        </AdminRoute>
                      )}
                    />
                    <Route
                      path="/project-tasks"
                      element={(
                        <AdminRoute>
                          <ProjectTasksPage />
                        </AdminRoute>
                      )}
                    />
                    <Route
                      path="/resource-planning"
                      element={(
                        <AdminRoute>
                          <ProjectPlanningPage />
                        </AdminRoute>
                      )}
                    />
                    <Route
                      path="/project-planning"
                      element={(
                        <ProjectPlanningRoute>
                          <CustomerProjectPlanningPage />
                        </ProjectPlanningRoute>
                      )}
                    />
                    <Route
                      path="/admin-api-keys"
                      element={(
                        <AdminRoute>
                          <AdminApiKeysPage />
                        </AdminRoute>
                      )}
                    />
                    <Route
                      path="/employees"
                      element={(
                        <AdminRoute>
                          <EmployeesPage />
                        </AdminRoute>
                      )}
                    />
                  </Routes>
                </div>
              </main>
            </div>
          </PrivateRoute>
          </div>
          <ToastContainer position="bottom-right" />
        </Router>
      </AuthProvider>
    </FluentProvider>
  );
}

export default App;
